import { useContext, useState, useCallback, useRef } from "react";

import { AuthContext } from "../contexts";
import { axiosInstance } from "../utils";

export const useAuth = () => {
  const {
    isLoggedIn,
    setAuthToken,
    isCheckingAuth,
    logout,
    user,
    setUser,
    team,
    setTeam,
    teamId,
    userId,
    setNangoSessionTokenLocalStorage,
    nangoSessionToken,
  } = useContext(AuthContext);

  const [userLoading, setUserLoading] = useState(false);
  const [userDetailsError, setUserDetailsError] = useState(null);
  const teamFetchAttempted = useRef(false);

  const fetchUserDetails = useCallback(async () => {
    if (user) return;

    setUserLoading(true);
    try {
      const response = await axiosInstance.get("/users/me/");

      if (response.status !== 200) {
        throw new Error("Failed to fetch user details");
      }

      setUser(response.data);
    } catch (err) {
      setUserDetailsError(err.message);
    } finally {
      setUserLoading(false);
    }
  }, [user, setUser]);

  const fetchTeamDetails = useCallback(
    async (forceRefresh = false) => {
      // Can't fetch without teamId
      if (!teamId) {
        return;
      }

      if (team && !forceRefresh) return;

      // Prevent infinite retry on failure
      if (!forceRefresh && teamFetchAttempted.current) return;
      teamFetchAttempted.current = true;

      setUserLoading(true);
      try {
        const response = await axiosInstance.get(`/teams/${teamId}/`);

        if (response.status !== 200) {
          throw new Error("Failed to fetch team details");
        }

        setTeam(response.data);
      } catch (err) {
        console.error("fetchTeamDetails error:", err);
        setUserDetailsError(err.message);
      } finally {
        setUserLoading(false);
      }
    },
    [teamId, team, setTeam],
  );

  // Convenience function to refresh feature flags
  const refreshFeatureFlags = async () => {
    return fetchTeamDetails(true);
  };

  const getOrCreateNangoSessionToken = async (id, email) => {
    if (nangoSessionToken) {
      return nangoSessionToken;
    }

    const nangoPostPayload = {
      end_user: {
        id: id.toString(),
        email,
      },
      organization: {
        id: teamId.toString(),
      },
      allowed_integrations: ["hubspot", "mixpanel", "amplitude", "posthog"],
    };

    const res = await axiosInstance.post(
      "common/nango/proxy/token/",
      nangoPostPayload,
    );

    setNangoSessionTokenLocalStorage(res.data.data.token);

    return res.data.data.token;
  };

  return {
    isLoggedIn,
    isCheckingAuth,
    setAuthToken,
    logout,
    apiClient: axiosInstance,
    user,
    setUser,
    userDetailsError,
    userLoading,
    teamId,
    userId,
    getOrCreateNangoSessionToken,
    nangoSessionToken,
    fetchUserDetails,
    team,
    fetchTeamDetails,
    refreshFeatureFlags,
  };
};
